import { Link } from 'react-router-dom';
import { Code2, ArrowRight, Star, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

function HeroSection() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2, // Trigger when 20% of the component is visible
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1c3d6e] via-[#1e3a8a] to-[#334E7D]">
      <div className="container mx-auto px-6">
        <div className="flex min-h-screen items-center">
          <div className="grid lg:grid-cols-2 lg:ml-16 gap-16 items-center w-full" ref={ref}>
            {/* Left Column - Content */}
            <motion.div
              className="space-y-8 lg:pr-12"
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <div className="inline-flex items-center mt-8 space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-white font-medium">
                  Rated 4.9/5 from over 1,200 developers
                </span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold text-white leading-[1.1]">
                Build faster with
                <span className="block mt-2 bg-gradient-to-r from-blue-200 to-white bg-clip-text text-transparent">
                  premium templates
                </span>
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed">
                Skip the setup, focus on your product. Our production-ready templates help you launch
                your next project in hours, not weeks.
              </p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 pt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <Link
                  to="/templates"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#1e3a8a] rounded-xl font-semibold hover:bg-blue-50 transition-colors group"
                >
                  Explore Templates
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/templates/installations/documentation"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/20 text-white rounded-xl backdrop-blur-sm hover:bg-white/10 transition-all"
                >
                  <Code2 className="w-5 h-5 mr-2" />
                  View the Docs
                </Link>
              </motion.div>

              <motion.div
                className="py-8  border-t border-white/10"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <div className="flex items-center space-x-4">
                  <div className="relative flex items-center">
                    <img
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=faces"
                      alt="Developer 1"
                      className="w-12 h-12 rounded-full border-2 border-white"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=64&h=64&fit=crop&crop=faces"
                      alt="Developer 2"
                      className="w-12 h-12 rounded-full border-2 border-white -ml-4"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=faces"
                      alt="Developer 3"
                      className="w-12 h-12 rounded-full border-2 border-white -ml-4"
                    />
                    <div className="w-12 h-12 rounded-full border-2 border-white -ml-4 bg-white/10 backdrop-blur-sm flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="text-white">
                    <p className="font-semibold">Join 12,000+ developers</p>
                    <p className="text-sm text-gray-300">who trust our templates</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Column - Preview */}
            <motion.div
              className="relative hidden lg:block"
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <div className="absolute -inset-4 bg-white/5 rounded-2xl w-[33.5rem] backdrop-blur-xl"></div>
              <div className="relative flex items-center justify-left">
                <img
                  src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&h=600&fit=crop"
                  alt="Modern Development Environment"
                  className="rounded-xl shadow-2xl border w-3/4 border-white/10"
                />
              </div>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#1c3d6e] to-[#334E7D] px-8 py-4 rounded-xl border border-white/10 backdrop-blur-sm shadow-xl">
                <p className="text-white font-medium">Ready-to-use components</p>
                <p className="text-sm text-gray-300">100+ components, fully customizable</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroSection;
